<!-- Header -->
@if (container) {
  <div class="container-header">
    <div #popoverContent class="container-info" test-id="container-data-info">
      <span class="container-label" test-id="container-name-label">
        {{ container.label }}
      </span>
      @if (sessionTimestamp) {
        <span class="session-timestamp" test-id="session-timestamp">
          {{ sessionTimestamp }}
        </span>
      }
    </div>
    <fw-popover action="mouseenter" position="below" [trigger]="popoverContent">
      <div class="container-info-popover padding-15">
        <strong>{{ container.label }}</strong>
        @if (sessionTimestamp) {
          <span class="bombay-grey">{{ sessionTimestamp }}</span>
        }
      </div>
    </fw-popover>
    <div class="container-actions">
      @if (tasksRefactorEnabled && containerType === ContainerType.Session && permissions.tasksModify) {
        <fw-button
          rightIcon="chevron-down"
          size="small"
          test-id="create-task-button"
          variant="outline"
          [disabled]="!dicomViewer.acquisitionIds.length"
          [fwMenuTriggerFor]="dicomViewer.acquisitionIds.length ? createTaskTmpl : null"
          [fwTooltip]="dicomViewer.acquisitionIds.length ? null : 'At least one DICOM file is required'"
        >
          Create Task
        </fw-button>
        <ng-template #createTaskTmpl>
          <fw-menu-container offset="6px" shadow="large" width="175px">
            <fw-menu>
              <fw-menu-item title="Reader" (click)="createReaderTask()" />
            </fw-menu>
          </fw-menu-container>
        </ng-template>
      }
      @if (containerType === ContainerType.Session && permissions.runGear) {
        <fw-button size="small" test-id="run-gear" variant="outline" (click)="runGear()"> Run Gear </fw-button>
      }
      @if (
        containerType === ContainerType.Session &&
        permissions.viewFiles &&
        dicomViewer.ohifEnabled &&
        dicomViewer.acquisitionIds.length > 0
      ) {
        <fw-button size="small" test-id="view-dicom-study" (click)="viewDicomStudy()"> Launch in Viewer </fw-button>
      }

      <fw-icon-button
        color="primary"
        icon="menu-vertical"
        size="small"
        test-id="container-options-menu"
        [fwMenuTriggerFor]="menu"
      />
      <ng-template #menu>
        <fw-menu-container>
          <fw-menu [multiSelect]="undefined">
            @if (containerType === ContainerType.Session && permissions.createContainer && permissions.uploadFiles) {
              <fw-menu-item
                test-id="create-new-acquisition"
                title="Create New Acquisition - Upload Data"
                (click)="createAcquisition()"
              />
            }
            @if (permissions.downloadFiles) {
              <fw-menu-item
                test-id="download-files"
                [title]="containerType === ContainerType.Session ? 'Download Session Files' : 'Download Subject Files'"
                (click)="downloadFiles()"
              />
            }

            @if (permissions.viewContainerMetadata) {
              @for (app of viewerApps; track app.name) {
                <fw-menu-item
                  title="Launch in {{ app.name }}"
                  [attr.test-id]="'viewer-app-' + app.name"
                  (click)="viewContainer(app)"
                />
              }
            }

            @if (containerType === ContainerType.Session && projectBids) {
              @let bidsView = bidsView$ | async;
              <fw-menu-item
                test-id="toggle-bids-view"
                title="BIDS View"
                [multiSelect]="true"
                [selected]="bidsView"
                [showCheckbox]="true"
                (click)="$any($event).stopPropagation?.(); toggleBidsView(!bidsView)"
              />
            }

            @let originalTimezone = state.originalTimezone$ | async;
            <fw-menu-item
              test-id="display-original-timezone"
              title="Display Original Timezone"
              [multiSelect]="true"
              [selected]="originalTimezone"
              [showCheckbox]="true"
              (click)="$any($event).stopPropagation?.(); toggleOriginalTimezones(!originalTimezone)"
            />

            @let runGearRulesOnMove = state.runGearRulesOnMove$ | async;
            <fw-menu-item
              fwTooltip="When enabled, the files you move and rename with this web browser will cause gear rules to be evaluated and jobs to be spawned, as if the files were newly uploaded."
              fwTooltipPosition="left"
              test-id="rerun-gear-rules"
              title="Run Gear Rules on File Move or Rename"
              [multiSelect]="true"
              [selected]="runGearRulesOnMove"
              [showCheckbox]="true"
              (click)="$any($event).stopPropagation?.(); toggleRunGearRules(!runGearRulesOnMove)"
            />

            @if (permissions.deleteContainer) {
              <fw-menu-separator />
              <fw-menu-item
                test-id="delete-container"
                [title]="containerType === ContainerType.Session ? 'Delete Session' : 'Delete Subject'"
                (click)="delete()"
              />
            }
          </fw-menu>
        </fw-menu-container>
      </ng-template>
    </div>
  </div>
}

<!-- Tabs -->
@if (container) {
  <mat-tab-group
    animationDuration="0ms"
    class="tabs-white tabs-compact"
    mat-stretch-tabs="false"
    [selectedIndex]="activeTabIndex$ | async"
    (selectedTabChange)="onTabChange($event)"
  >
    <!-- Acquisitions -->
    @if (containerType === 'session') {
      <mat-tab active label="Acquisitions" [attr.tab-id]="'data'">
        <!-- Not lazy, to detect DICOM files and show View DICOM Study button -->
        <session-data
          class="tab-content"
          [collectionId]="collectionId$.value"
          [project]="project"
          [session]="session"
        />
      </mat-tab>
    }

    <!-- Session -->
    @if (containerType === 'session') {
      <mat-tab label="Session" [attr.tab-id]="'annotation'">
        <ng-template matTabContent>
          <div class="tab-content animated fadeInLeft">
            <session-info [permissions]="projectPermissions" [session]="session" (afterSave)="reloadSessions()" />

            <container-applications appType="form" [container]="session" (afterChange)="loadContainer()" />

            @if (permissions.viewContainerMetadata) {
              <info-edit
                class="restricted-height"
                [container]="session"
                [info]="session?.info"
                [writable]="permissions.editContainerMetadata"
                (afterSave)="session.info = $event"
              />
            }

            @if (permissions.viewFileMetadata) {
              <legacy-container-attachments [container]="session" (afterChange)="loadContainer()" />
            }

            @if (permissions.viewTags) {
              <fw-tags [container]="session" (afterSave)="session.tags = $event" />
            }

            @if (permissions.viewNotes) {
              <container-notes [container]="session" [notes]="session?.notes" (afterSave)="session.notes = $event" />
            }

            <fw-save-bar />
          </div>
        </ng-template>
      </mat-tab>
    }

    <!-- Subject -->
    <mat-tab label="Subject" [attr.tab-id]="'subject'">
      <ng-template matTabContent>
        <div class="tab-content animated fadeInLeft">
          <subject-info
            style="margin-bottom: 20px"
            [permissions]="projectPermissions"
            [subject]="subject"
            (afterSave)="loadContainer()"
          />

          <container-applications appType="form" [container]="subject" (afterChange)="loadContainer()" />

          @if (permissions.viewContainerMetadata) {
            <info-edit
              class="restricted-height"
              [container]="subject"
              [info]="subject?.info"
              [writable]="permissions.editContainerMetadata"
              (afterSave)="subject.info = $event"
            />
          }

          @if (permissions.viewFileMetadata) {
            <legacy-container-attachments [container]="subject" (afterChange)="loadContainer()" />
          }

          @if (permissions.viewTags) {
            <fw-tags [container]="subject" (afterSave)="subject.tags = $event" />
          }

          @if (permissions.viewNotes) {
            <container-notes [container]="subject" [notes]="subject?.notes" (afterSave)="subject.notes = $event" />
          }

          <fw-save-bar />
        </div>
      </ng-template>
    </mat-tab>

    <!-- Sessions -->
    @if (containerType === 'subject') {
      <mat-tab label="Sessions" [attr.tab-id]="'sessions'">
        <ng-template matTabContent>
          <subject-sessions class="tab-content" [subject]="subject" />
        </ng-template>
      </mat-tab>
    }

    <!-- Project -->
    <mat-tab label="Project" [attr.tab-id]="'project'">
      <ng-template matTabContent>
        @if (project) {
          <div class="tab-content animated fadeInLeft">
            <project-info
              style="margin-bottom: 20px"
              [container]="container"
              [group]="group"
              [permissions]="projectPermissions"
              [projectStats]="projectStats"
              [project]="project"
              (afterSave)="loadContainer()"
            />

            <container-applications appType="form" [container]="subject" (afterChange)="loadContainer()" />

            @if (permissions.viewContainerMetadata) {
              <info-edit
                class="restricted-height"
                [container]="project"
                [info]="project?.info"
                [writable]="permissions.editContainerMetadata"
                (afterSave)="project.info = $event; projectsService.reloadProject$.next()"
              />
            }

            @if (permissions.viewFileMetadata) {
              <legacy-container-attachments
                [container]="project"
                (afterChange)="loadProject(); projectsService.reloadProject$.next()"
              />
            }

            @if (permissions.viewTags) {
              <fw-tags
                [container]="project"
                (afterSave)="project.tags = $event; projectsService.reloadProject$.next()"
              />
            }

            @if (permissions.viewNotes) {
              <container-notes
                [container]="project"
                [notes]="project?.notes"
                (afterSave)="project.notes = $event; projectsService.reloadProject$.next()"
              />
            }

            <fw-save-bar />
          </div>
        } @else {
          <ng-container *ngTemplateOutlet="loadingState" />
        }
      </ng-template>
    </mat-tab>

    <!-- Collection -->
    @if (collectionId$.value) {
      <mat-tab label="Collection" [attr.tab-id]="'collection'">
        <ng-template matTabContent>
          <div class="tab-content animated fadeInLeft">
            @if (collection) {
              <div class="card">
                <div class="card-header compact">
                  <h1 class="ellipsis">{{ collection.label || 'No Collection Label' }}</h1>
                </div>
                <div style="margin: -15px -20px -18px">
                  <div class="info-row">
                    <span class="field-tooltip"></span>
                    <span class="field-label">Description</span>
                    <div class="field-value" style="flex: 2">
                      <span>{{ $any(collection).description }}</span>
                    </div>
                  </div>
                  <div class="info-row">
                    <span class="field-tooltip"></span>
                    <span class="field-label">Curator</span>
                    <div class="field-value" style="flex: 2">
                      <span>{{ collection.curator }}</span>
                    </div>
                  </div>
                  <div class="info-row">
                    <span class="field-tooltip"></span>
                    <a color="primary" mat-flat-button test-id="view-collection-link" [href]="collectionHref">
                      View Collection
                    </a>
                  </div>
                </div>
              </div>
            }
            @if (collectionRoleMatrix?.ro) {
              <legacy-container-attachments [container]="collection" (afterChange)="loadCollection()" />
              <container-notes
                [container]="collection"
                [notes]="collection?.notes"
                (afterSave)="collection.notes = $event"
              />
            }
          </div>
        </ng-template>
      </mat-tab>
    }

    <!-- Analyses -->
    @if (permissions.viewAnalyses) {
      <mat-tab label="Analyses" [attr.tab-id]="'analyses'">
        <ng-template matTabContent>
          <analysis-list [containerType]="analysesContainerType" [container]="analysesContainer" />
        </ng-template>
      </mat-tab>
    }

    <!-- Provenance -->
    @if (permissions.viewJobs) {
      <mat-tab label="Provenance" [attr.tab-id]="'provenance'">
        <ng-template matTabContent>
          <job-provenance [container]="container" />
        </ng-template>
      </mat-tab>
    }
  </mat-tab-group>
} @else {
  <ng-container *ngTemplateOutlet="loadingState" />
}

<ng-template #loadingState>
  @if (containerId$.value) {
    @if (!error) {
      <div class="loading-state">
        <mat-progress-bar mode="buffer" />
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="errorState" />
    }
  }
</ng-template>

<ng-template #emptyState>
  <!-- Blank pane -->
</ng-template>

<ng-template #errorState>
  <div class="info-message">
    <div class="fab">
      <mat-icon>error_outline</mat-icon>
    </div>
    @switch (error.status) {
      @case (403) {
        <p>You do not have permission to view this {{ containerType }}.</p>
      }
      @case (404) {
        <p>{{ containerType | titlecase }} {{ containerId$.value }} does not exist.</p>
      }
      @default {
        <p>{{ containerType | titlecase }} data could not be loaded.</p>
        <button color="primary" mat-flat-button test-id="try-again-button" (click)="loadContainer()">Try Again</button>
      }
    }
  </div>
</ng-template>
